/**
 * W4e — reserved-window schedule conflicts on the Dayboard (founder decisions 2 and 5, 2026-10-18).
 *
 * The server marks a ticket the moment an approved rollover's reserved time grows over it
 * (schedule_state 'conflict' / reason 'reserved_window_overlap') and moves it to Needs Placement ('unplaced') if no
 * lane walk re-plans it within the recovery window. This panel lists those tickets for the people who act on the
 * schedule: approvers AND operational dispatchers. The audience is the SERVER's (list_reserved_window_conflicts →
 * is_schedule_conflict_viewer); a technician or another shop is refused and the panel stays hidden.
 *
 * Read-only on purpose: it never decides a rollover (dispatchers cannot; decisions stay in RolloverDecisionPanel,
 * approver-only) and writes nothing. TicketCard is pinned byte-identical (W5), so the conflict is surfaced here.
 *
 * B-W17 (LEDGER B-W13: "keep the protected commitment visible in a conflicted state"): the same server list also
 * returns a protected Waiter / appointment the D-2 expiry or cleanup KEPT in place ('conflict' /
 * 'day_ended_unapproved', kind 'protected_commitment_kept'). It is shown here as kept — its promise unchanged, its
 * time still reserved, a person must act — still read-only for everyone.
 */
import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { describeScheduleState } from '@/utils/scheduleStateLabels';
import { useScheduleSignalRefresh } from '@/hooks/useScheduleSignalRefresh';
import { useLatestResponseGuard } from '@/hooks/useLatestResponseGuard';
import { getScheduleDock } from '@/components/scheduling/scheduleDock';

export interface ReservedWindowConflictRow {
  ticket_id: string;
  simple_ticket_number: number | null;
  state: string;
  reason: string | null;
  scheduled_date: string | null;
  technician_id: string | null;
  start_time: string | null;
  opti_finish: string | null;
  since: string | null;
  /** B-W17: 'reserved_window_overlap' or 'protected_commitment_kept' (older servers omit it). */
  kind?: string | null;
  /** 'waiter' | 'appointment' for a kept protected commitment. */
  protected_commitment?: string | null;
  source?: string | null;
}

export function isKeptProtectedCommitment(r: Pick<ReservedWindowConflictRow, 'kind' | 'state' | 'reason'>): boolean {
  return r.kind === 'protected_commitment_kept' || (r.state === 'conflict' && r.reason === 'day_ended_unapproved');
}

/** Words for one row (pure). */
export function describeConflictRow(r: ReservedWindowConflictRow): { label: string; detail: string } {
  if (isKeptProtectedCommitment(r)) {
    const what = r.protected_commitment === 'waiter' ? 'Waiter' : r.protected_commitment === 'appointment' ? 'customer appointment' : 'protected commitment';
    return {
      label: `Protected ${what} kept — the day ended without a decision`,
      detail: 'Kept in place: the customer promise is unchanged and this time stays reserved. A person must reassign it or reschedule it with the customer.',
    };
  }
  const p = describeScheduleState(r.state, r.reason);
  return { label: p.label, detail: p.detail };
}

// The generated types do not know the W4e RPC yet; one narrow cast, the client is never detached.
type LooseRpc = { rpc: (f: string, a: Record<string, unknown>) => PromiseLike<{ data: unknown; error: unknown }> };

export function ScheduleConflictPanel() {
  const { activeShop } = useAuth();
  const shopId = activeShop?.id ?? null;
  const [rows, setRows] = useState<ReservedWindowConflictRow[]>([]);
  const [allowed, setAllowed] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const { begin } = useLatestResponseGuard();   // Issue #26: an older answer never replaces a newer one

  const load = useCallback(async () => {
    if (!shopId) return;
    const isLatest = begin();
    try {
      const { data, error } = await (supabase as unknown as LooseRpc).rpc('list_reserved_window_conflicts', { p_shop: shopId });
      if (!isLatest()) return;
      if (error) {
        // 42501 = not an approver / dispatcher of this shop: hide. Any other read failure keeps the last list.
        if ((error as { code?: string }).code === '42501') { setAllowed(false); setRows([]); }
        return;
      }
      setAllowed(true);
      setRows(Array.isArray(data) ? (data as ReservedWindowConflictRow[]) : []);
    } catch {
      /* a failed READ keeps the last list; nothing is written */
    }
  }, [shopId, begin]);

  useEffect(() => {
    if (!shopId) return;
    void load();
    const t = setInterval(() => { void load(); }, 60_000);
    return () => clearInterval(t);
  }, [shopId, load]);
  // GAPS-2: also re-read on realtime rejoin / page return / back online — not only every 60 s.
  useScheduleSignalRefresh(() => { void load(); }, { enabled: !!shopId });

  if (!allowed || rows.length === 0 || typeof document === 'undefined') return null;
  const kept = rows.filter(isKeptProtectedCommitment).length;
  const needsPlacement = rows.filter((r) => r.state === 'unplaced').length;
  const inConflict = rows.length - needsPlacement - kept;
  const summary = [
    inConflict > 0 ? `${inConflict} in schedule conflict` : null,
    needsPlacement > 0 ? `${needsPlacement} need${needsPlacement === 1 ? 's' : ''} placement` : null,
    kept > 0 ? `${kept} protected commitment${kept === 1 ? '' : 's'} kept` : null,
  ].filter(Boolean).join(' · ');
  const heading = kept > 0 ? 'Schedule conflicts' : 'Reserved rollover time overlap';

  return createPortal((
    <div role="region" aria-label="Schedule conflicts" data-testid="schedule-conflict-panel"
      className="pointer-events-auto order-1 w-full max-w-[26rem] rounded-lg border-2 border-red-500 bg-red-50 text-red-900 shadow-lg dark:bg-red-950 dark:text-red-100">
      <button type="button" className="flex w-full items-center justify-between px-4 py-2 text-left font-medium"
        onClick={() => setExpanded((e) => !e)} aria-expanded={expanded} data-testid="schedule-conflict-toggle">
        <span>{heading}: {summary}</span>
        <span aria-hidden>{expanded ? '▾' : '▸'}</span>
      </button>
      {expanded && (
        <ul className="max-h-[22vh] lg:max-h-[50vh] space-y-2 overflow-y-auto px-4 pb-4 text-sm">
          {rows.map((r) => {
            const p = describeConflictRow(r);
            return (
              <li key={r.ticket_id} data-testid="schedule-conflict-row" data-state={r.state}
                data-kind={isKeptProtectedCommitment(r) ? 'protected_commitment_kept' : 'reserved_window_overlap'}>
                <div className="font-medium">
                  {r.simple_ticket_number ? `#${r.simple_ticket_number}` : r.ticket_id}
                  {r.scheduled_date ? ` · ${r.scheduled_date}` : ''}
                </div>
                <div>{p.label}</div>
                <div className="opacity-80">{p.detail}</div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  ), getScheduleDock()); // GAPS-6: shared dock — never stacked on top of the decision panel at phone width
}
